import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { type UnprocessedEvent, type UnprocessedEventState } from "@/lib/types";
import {
  errorCodeText,
  isScanStale,
  missingText,
  REFUSAL_LABEL,
  RESOLUTION_LABEL,
  RETRY_BLOCK_LABEL,
} from "@/lib/unprocessedEvent";

const PAGE_SIZE = 50;

export const UNPROCESSED_EVENTS_KEY = "unprocessed-events";
export const UNPROCESSED_EVENTS_OPEN_COUNT_KEY = [
  UNPROCESSED_EVENTS_KEY,
  "open-count",
] as const;

export const useOpenUnprocessedEventsCount = () =>
  useQuery({
    queryKey: UNPROCESSED_EVENTS_OPEN_COUNT_KEY,
    queryFn: () => api.listUnprocessedEvents("open", 0, 0),
  });

export const UnprocessedEvents = () => {
  const [state, setState] = useState<UnprocessedEventState>("open");
  const [pageIndex, setPageIndex] = useState<number>(0);
  const offset = pageIndex * PAGE_SIZE;

  const practices = useQuery({
    queryKey: ["practices"],
    queryFn: api.listPractices,
  });
  const openCount = useOpenUnprocessedEventsCount();
  const eventsQuery = useQuery({
    queryKey: [UNPROCESSED_EVENTS_KEY, state, offset],
    queryFn: () => api.listUnprocessedEvents(state, PAGE_SIZE, offset),
  });

  const practiceNameById = useMemo(() => {
    const names = new Map<string, string>();
    practices.data?.forEach((practice) =>
      names.set(practice.id, practice.displayName),
    );
    return names;
  }, [practices.data]);

  const selectState = (next: UnprocessedEventState) => {
    setState(next);
    setPageIndex(0);
  };

  const total = eventsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-4">
        <h2 className="text-2xl tracking-tight text-[#1a2b6b]">
          Nezpracované události
        </h2>
        {eventsQuery.isSuccess && (
          <LastScan lastScanAt={eventsQuery.data.lastScanAt} />
        )}
      </div>

      <div className="mb-4 flex gap-2">
        <FilterChip
          active={state === "open"}
          onClick={() => selectState("open")}
        >
          Otevřené
          {(openCount.data?.total ?? 0) > 0 && (
            <span className="ml-2 rounded-full bg-red-600 px-1.5 text-xs text-white">
              {openCount.data?.total}
            </span>
          )}
        </FilterChip>
        <FilterChip
          active={state === "resolved"}
          onClick={() => selectState("resolved")}
        >
          Vyřešené
        </FilterChip>
      </div>

      <EventsContent
        state={state}
        isPending={eventsQuery.isPending}
        error={eventsQuery.error}
        rows={eventsQuery.data?.rows}
        practiceNameById={practiceNameById}
      />

      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-end gap-3 text-sm text-gray-600">
          <button
            type="button"
            onClick={() => setPageIndex((index) => Math.max(0, index - 1))}
            disabled={pageIndex === 0}
            className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
          >
            ← Předchozí
          </button>
          <span className="font-mono text-xs text-gray-500">
            {pageIndex + 1} / {pageCount}
          </span>
          <button
            type="button"
            onClick={() =>
              setPageIndex((index) => Math.min(pageCount - 1, index + 1))
            }
            disabled={pageIndex + 1 >= pageCount}
            className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
          >
            Další →
          </button>
        </div>
      )}
    </div>
  );
};

const EventsContent = ({
  state,
  isPending,
  error,
  rows,
  practiceNameById,
}: {
  state: UnprocessedEventState;
  isPending: boolean;
  error: Error | null;
  rows: UnprocessedEvent[] | undefined;
  practiceNameById: Map<string, string>;
}) => {
  if (isPending) {
    return <p className="text-gray-500">Načítám…</p>;
  }

  if (error || !rows) {
    return (
      <p className="text-red-600">Chyba: {error?.message ?? "neznámá chyba"}</p>
    );
  }

  if (rows.length === 0) {
    return <p className="text-gray-500">Žádné nezpracované události</p>;
  }

  return (
    <div className="overflow-x-auto rounded border border-gray-200 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
          <tr>
            <th className="px-4 py-3 font-medium">Kdy</th>
            <th className="px-4 py-3 font-medium">Ordinace</th>
            <th className="px-4 py-3 font-medium">Co chybí</th>
            <th className="px-4 py-3 font-medium">Pokusy</th>
            <th className="px-4 py-3 font-medium">Chyba</th>
            <th className="px-4 py-3 font-medium">
              {state === "open" ? "" : "Vyřešeno"}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((event) => (
            <tr key={event.id} className="align-top">
              <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">
                {formatDate(event.occurredAt)}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                {event.practiceId
                  ? (practiceNameById.get(event.practiceId) ?? event.practiceId)
                  : "—"}
              </td>
              <td className="min-w-[18rem] px-4 py-3 text-gray-800">
                {missingText(event)}
                {event.callRowId && (
                  <Link
                    to={`/calls/${event.callRowId}`}
                    className="ml-2 whitespace-nowrap text-xs text-[#1a2b6b] hover:underline"
                  >
                    Detail hovoru →
                  </Link>
                )}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-gray-700">
                {event.attempts}
              </td>
              <td className="px-4 py-3 text-xs text-gray-700">
                <ErrorCell event={event} />
              </td>
              <td className="min-w-[14rem] px-4 py-3 text-right">
                {state === "open" ? (
                  <OpenActions event={event} />
                ) : (
                  <Resolution event={event} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ErrorCell = ({ event }: { event: UnprocessedEvent }) => (
  <>
    {event.errorCode ? (
      <span title={event.errorCode}>{errorCodeText(event.errorCode)}</span>
    ) : (
      "—"
    )}
    {event.lastRetryError && (
      <p className="mt-1 text-gray-500" title={event.lastRetryError}>
        Poslední pokus: {errorCodeText(event.lastRetryError)}
      </p>
    )}
  </>
);

const OpenActions = ({ event }: { event: UnprocessedEvent }) => {
  const qc = useQueryClient();
  const onSettled = () => {
    void qc.invalidateQueries({ queryKey: [UNPROCESSED_EVENTS_KEY] });
  };
  const retry = useMutation({
    mutationFn: () => api.retryUnprocessedEvent(event.id, event.generation),
    onSettled,
  });
  const resolve = useMutation({
    mutationFn: () => api.resolveUnprocessedEvent(event.id, event.generation),
    onSettled,
  });
  const isPending = retry.isPending || resolve.isPending;
  const refusal = retry.error ?? resolve.error;

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex flex-wrap justify-end gap-2">
        <RetryControl
          event={event}
          disabled={isPending}
          onRetry={() => retry.mutate()}
          isPending={retry.isPending}
        />
        <button
          type="button"
          onClick={() => resolve.mutate()}
          disabled={isPending}
          className="whitespace-nowrap rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {resolve.isPending ? "Ukládám…" : "Označit jako vyřešené"}
        </button>
      </div>
      {refusal && (
        <p className="text-xs text-red-700">
          {REFUSAL_LABEL[refusal.message] ?? refusal.message}
        </p>
      )}
    </div>
  );
};

const RetryControl = ({
  event,
  disabled,
  isPending,
  onRetry,
}: {
  event: UnprocessedEvent;
  disabled: boolean;
  isPending: boolean;
  onRetry: () => void;
}) => {
  if (!event.canRetry) {
    return (
      <span className="self-center text-xs text-gray-600">
        {event.retryBlockedReason
          ? RETRY_BLOCK_LABEL[event.retryBlockedReason]
          : ""}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onRetry}
      disabled={disabled}
      className="whitespace-nowrap rounded border border-[#1a2b6b] bg-[#1a2b6b] px-3 py-1.5 text-sm text-white hover:opacity-90 disabled:opacity-50"
    >
      {isPending ? "Zpracovávám…" : "Zpracovat znovu"}
    </button>
  );
};

const Resolution = ({ event }: { event: UnprocessedEvent }) => (
  <span className="text-xs text-gray-600">
    {[
      event.resolvedBy === "system" ? "automaticky" : event.resolvedBy,
      event.resolvedAt && formatDate(event.resolvedAt),
      event.resolution && RESOLUTION_LABEL[event.resolution],
    ]
      .filter(Boolean)
      .join(", ")}
  </span>
);

const LastScan = ({ lastScanAt }: { lastScanAt: string | null }) => {
  const now = Date.now();
  const stale = isScanStale(lastScanAt, now);
  return (
    <p
      className={`text-sm ${stale ? "font-medium text-red-700" : "text-gray-500"}`}
    >
      Poslední kontrola běhů:{" "}
      {lastScanAt ? relativeTime(lastScanAt, now) : "zatím neproběhla"}
    </p>
  );
};

const FilterChip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={
      "flex items-center rounded-full border px-3 py-1 text-sm " +
      (active
        ? "border-[#1a2b6b] bg-[#1a2b6b] text-white"
        : "border-gray-300 bg-white text-[#1a2b6b] hover:bg-gray-50")
    }
  >
    {children}
  </button>
);

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const relativeTime = (iso: string, now: number): string => {
  const elapsed = Math.max(0, now - new Date(iso).getTime());
  if (elapsed < MINUTE_MS) {
    return "právě teď";
  }
  if (elapsed < HOUR_MS) {
    return `před ${Math.floor(elapsed / MINUTE_MS)} min`;
  }
  if (elapsed < DAY_MS) {
    return `před ${Math.floor(elapsed / HOUR_MS)} h`;
  }
  return formatDate(iso);
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};
